/**
 * The rules for a bill-service tile: the letter mark, the colour behind it, and the raster
 * icon that may replace both.
 *
 * ## Why this is not `checkImageFile`
 *
 * `emailAttachments.ts::checkImageFile` is the right *shape* and the wrong *rules*. It allows
 * `image/gif` and carries the 2 MB mail ceiling, because an animated banner in an email is
 * fine and mail is delivered over SMTP. A bill icon is neither: it is a small square mark
 * served from a public route to every customer's phone, and the contract gives it its own,
 * much tighter numbers — `BILL_ICON_MAX_BYTES` is 200 KB, with the blunt reason that a
 * service chip is drawn at 96 px and anything larger is a mistake rather than a choice. The
 * refusal *wording* is deliberately the same, because an operator should not have to learn
 * two dialects of "that file is too big", and `ImageCheck` and `formatBytes` are imported
 * rather than reimplemented so there is exactly one of each.
 *
 * ## Why there is no SVG here, in either list
 *
 * An SVG is an active document. It can carry script and external references, it executes
 * when it is displayed, and these bytes are served to a mobile app and rendered in this
 * console. The contract makes the same call twice already, for avatars and for brand assets,
 * with the same one-line reason. A "logo" that runs code is not a logo.
 *
 * ## Why the console sniffs the bytes as well as reading the type
 *
 * `File.type` is the browser's guess from the file extension. Renaming `payload.svg` to
 * `payload.png` changes it. The API sniffs the real magic number and is the authority, but
 * the console reads the whole file into base64 anyway to upload it, so checking the first
 * few bytes costs nothing and turns a server rejection after a megabyte on the wire into an
 * immediate, specific sentence. Nothing here relaxes a server rule.
 */
import type { BillIconMime } from '../contracts/types.js';
import { formatBytes, type ImageCheck } from './emailAttachments.js';

/**
 * Mirrors the contract's `BILL_ICON_MAX_BYTES`.
 *
 * 200 KB, which is far below the 1 MB a brand logo gets, and deliberately: a service chip is
 * rendered at 96 px on a phone. Repeated here only so an operator learns their 4 MB export is
 * too large before uploading it rather than after; the API decodes and enforces it again.
 */
export const BILL_ICON_MAX_BYTES = 200_000;

/** Raster only. Mirrors the contract's `BILL_ICON_MIMES`. */
export const BILL_ICON_MIMES = ['image/png', 'image/jpeg', 'image/webp'] as const satisfies readonly BillIconMime[];

export const BILL_ICON_ACCEPT = BILL_ICON_MIMES.join(',');

export const BILL_ICON_TYPE_LABEL = 'PNG, JPG or WebP';

/** The contract's `billServiceSchema` bounds, mirrored so the field can refuse early. */
export const BILL_MARK_MAX = 6;
export const BILL_MARK_MIN = 1;

export function isBillIconMime(mime: string): mime is BillIconMime {
  return (BILL_ICON_MIMES as readonly string[]).includes(mime);
}

/**
 * Whether a chosen file may be uploaded, judged on what the browser knows before any bytes
 * are read. Every refusal names the limit and the actual value.
 */
export function checkBillIconFile(file: {
  readonly name: string;
  readonly type: string;
  readonly size: number;
}): ImageCheck {
  if (!isBillIconMime(file.type)) {
    return {
      ok: false,
      error: `${BILL_ICON_TYPE_LABEL} only. That file is ${file.type === '' ? 'an unknown type' : file.type}.`,
    };
  }
  if (file.size <= 0) return { ok: false, error: 'That file is empty.' };
  if (file.size > BILL_ICON_MAX_BYTES) {
    return {
      ok: false,
      error: `Keep the icon under ${formatBytes(BILL_ICON_MAX_BYTES)}. That one is ${formatBytes(file.size)}.`,
    };
  }
  return { ok: true };
}

/**
 * What the first bytes of a file actually are, regardless of what it is called.
 *
 * Returns `'refused'` rather than `null` for the formats that are recognisably *not* raster
 * images, so the console can say "that is an SVG" instead of the useless "unknown type".
 * SVG, XML and HTML are all called out by name because they are the ones somebody renames a
 * file to when they want it served.
 */
export function sniffBillIconMime(bytes: Uint8Array): BillIconMime | 'refused' | null {
  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return 'image/png';
  }
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (
    bytes.length >= 12 &&
    ascii(bytes, 0, 4) === 'RIFF' &&
    ascii(bytes, 8, 12) === 'WEBP'
  ) {
    return 'image/webp';
  }
  const head = ascii(bytes, 0, Math.min(bytes.length, 64)).trim().toLowerCase();
  if (
    head.startsWith('<svg') ||
    head.startsWith('<?xml') ||
    head.startsWith('<!doctype') ||
    head.startsWith('<html') ||
    head.startsWith('%pdf') ||
    head.startsWith('gif8')
  ) {
    return 'refused';
  }
  return null;
}

function ascii(bytes: Uint8Array, from: number, to: number): string {
  let out = '';
  for (let i = from; i < to && i < bytes.length; i += 1) out += String.fromCharCode(bytes[i] ?? 0);
  return out;
}

/**
 * The sniff result as a refusal an operator can act on, or `{ ok: true }`.
 *
 * A file whose bytes are unrecognisable is *not* refused here: the server sniffs properly and
 * a console that guessed wrong would block a legitimate upload with no way around it. What
 * is refused is a file whose bytes are recognisably something else, which is the case that
 * matters.
 */
export function checkBillIconBytes(bytes: Uint8Array, declaredMime: string): ImageCheck {
  const sniffed = sniffBillIconMime(bytes);
  if (sniffed === 'refused') {
    return {
      ok: false,
      error: `That file is not a ${BILL_ICON_TYPE_LABEL} image whatever it is named. Icons are rendered in the app, so a document that can run when it is displayed is refused.`,
    };
  }
  if (sniffed !== null && sniffed !== declaredMime) {
    return {
      ok: false,
      error: `That file is named ${declaredMime} but its contents are ${sniffed}. Re-export it, or rename it correctly.`,
    };
  }
  return { ok: true };
}

/** The mark, judged against `billServiceSchema`'s 1 to 6 characters. */
export function checkBillMark(mark: string): ImageCheck {
  const trimmed = mark.trim();
  if (trimmed.length < BILL_MARK_MIN) {
    return { ok: false, error: 'A tile needs a mark. One to six characters, like IE or DStv.' };
  }
  if ([...trimmed].length > BILL_MARK_MAX) {
    return { ok: false, error: `Keep the mark to ${String(BILL_MARK_MAX)} characters or fewer.` };
  }
  return { ok: true };
}

/** `#RRGGBB`, upper-cased, or null. The same shape `hexColorSchema` demands. */
export function normaliseBillColor(value: string): string | null {
  const match = /^#?([0-9A-Fa-f]{6})$/.exec(value.trim());
  return match === null ? null : `#${(match[1] ?? '').toUpperCase()}`;
}

export function checkBillColor(value: string): ImageCheck {
  return normaliseBillColor(value) === null
    ? { ok: false, error: 'A six-digit hex colour, like #1E88E5.' }
    : { ok: true };
}

/**
 * Black or white text on the tile, whichever the operator's colour can actually be read
 * against.
 *
 * The tile paints its own opaque background, so this is theme-independent: the same colour
 * looks the same in the console's light and dark themes and on the customer's phone. The
 * threshold is the standard relative-luminance one rather than a full WCAG ratio, because
 * the choice is binary and between the two extremes — the ratio would only ever confirm
 * which of black and white is further away.
 */
export function billMarkColor(background: string): '#000000' | '#FFFFFF' {
  const hex = normaliseBillColor(background);
  if (hex === null) return '#FFFFFF';
  const channels = [1, 3, 5].map((i) => Number.parseInt(hex.slice(i, i + 2), 16) / 255);
  const linear = channels.map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
  const luminance = 0.2126 * (linear[0] ?? 0) + 0.7152 * (linear[1] ?? 0) + 0.0722 * (linear[2] ?? 0);
  return luminance > 0.45 ? '#000000' : '#FFFFFF';
}
